import { getJSON, postJSON } from 'common/services/api'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { OverrideLevel } from '../types'
import {
  PAY_RATE_CARD_DELETE_FUTURE_VERSION_QUERY_KEY,
  PAY_RATE_CARD_HISTORY_QUERY_KEY,
  PAY_RATE_CARDS_QUERY_KEY,
  PAY_RATE_CARDS_UPDATE_QUERY_KEY,
  PAY_RATE_CARDS_URL,
  PAY_RATES_SETTINGS_QUERY_KEY,
  PAY_RATES_SETTINGS_UPDATE_QUERY_KEY,
  PAY_RATES_SETTINGS_URL
} from './queryConstants'

export { OverrideLevel }

export enum RateType {
  HOURLY = 'HOURLY',
  EVENT = 'EVENT'
}

export interface RateOverride {
  eventTypeId: string | null
  level: OverrideLevel
  rate: string
  rateType: RateType
}

export interface PaymentCardTeamMember {
  id: string
  picture?: string
  name: { first: string; last: string }
}

export interface PaymentCard {
  _id: string
  homeId: string
  name: string
  effectiveFrom?: string
  rates: RateOverride[]
  deleted: boolean
  supersededAt: string | null
  previousVersionId: string | null
  initialId?: string
  previousVersion?: PaymentCard
  teamMembers?: PaymentCardTeamMember[]
  createdAt: string
  updatedAt: string
}

export interface RateOverrideDto {
  eventTypeId?: string | null
  level: OverrideLevel
  rate: string
  rateType: RateType
}

export interface PaymentCardDto {
  _id?: string
  name: string
  effectiveFrom: string
  rates: RateOverrideDto[]
  deleted?: boolean
  initialId?: string
}

export interface UpdatePaymentCardsDto {
  cards: PaymentCardDto[]
}

export const useGetPaymentCards = (enabled = true) => {
  return useQuery<PaymentCard[]>(
    [PAY_RATE_CARDS_QUERY_KEY],
    async () => {
      const response = await getJSON({
        url: PAY_RATE_CARDS_URL,
        isRota: true
      })
      return response
    },
    { enabled }
  )
}

export const useUpdatePaymentCards = () => {
  const queryClient = useQueryClient()

  const updateFunction = async (dto: UpdatePaymentCardsDto): Promise<void> => {
    await postJSON({
      url: PAY_RATE_CARDS_URL,
      method: 'PUT',
      json: dto,
      responseType: 'text',
      isRota: true
    })
  }

  const { mutateAsync: updatePaymentCards, isLoading: isUpdating } =
    useMutation<void, unknown, UpdatePaymentCardsDto, unknown>(
      [PAY_RATE_CARDS_UPDATE_QUERY_KEY],
      updateFunction,
      {
        onSuccess: () => {
          queryClient.invalidateQueries([PAY_RATE_CARDS_QUERY_KEY])
          queryClient.invalidateQueries([PAY_RATE_CARD_HISTORY_QUERY_KEY])
        }
      }
    )

  return {
    updatePaymentCards,
    isUpdating
  }
}

export const useDeletePayRateCardFutureVersion = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: deletePayRateCardFutureVersion, isLoading: isDeleting } =
    useMutation<void, unknown, { initialId: string }>(
      [PAY_RATE_CARD_DELETE_FUTURE_VERSION_QUERY_KEY],
      async ({ initialId }) => {
        await postJSON({
          url: `${PAY_RATE_CARDS_URL}/${initialId}`,
          method: 'DELETE',
          responseType: 'text',
          isRota: true
        })
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([PAY_RATE_CARDS_QUERY_KEY])
          queryClient.invalidateQueries([PAY_RATE_CARD_HISTORY_QUERY_KEY])
        }
      }
    )

  return { deletePayRateCardFutureVersion, isDeleting }
}

export interface PayRatesSettings {
  enabled: boolean
}

export const useGetPayRatesSettings = () => {
  return useQuery<PayRatesSettings>(
    [PAY_RATES_SETTINGS_QUERY_KEY],
    async () => {
      try {
        const response = await getJSON({
          url: PAY_RATES_SETTINGS_URL,
          isRota: true
        })
        return response ?? { enabled: false }
      } catch {
        return { enabled: false }
      }
    },
    { staleTime: 30_000, refetchOnWindowFocus: false, retry: false }
  )
}

export const useUpdatePayRatesSettings = () => {
  const queryClient = useQueryClient()

  const updateFunction = async (
    dto: PayRatesSettings
  ): Promise<PayRatesSettings> => {
    return postJSON({
      url: PAY_RATES_SETTINGS_URL,
      method: 'PUT',
      json: dto,
      isRota: true
    })
  }

  const { mutateAsync: updatePayRatesSettings, isLoading: isUpdating } =
    useMutation<PayRatesSettings, unknown, PayRatesSettings, unknown>(
      [PAY_RATES_SETTINGS_UPDATE_QUERY_KEY],
      updateFunction,
      {
        onSuccess: () => {
          queryClient.invalidateQueries([PAY_RATES_SETTINGS_QUERY_KEY])
        }
      }
    )

  return {
    updatePayRatesSettings,
    isUpdating
  }
}

export const useGetPaymentCardHistory = (cardId?: string) => {
  return useQuery<PaymentCard[]>(
    [PAY_RATE_CARD_HISTORY_QUERY_KEY, cardId],
    async () => {
      const response = await getJSON({
        url: `${PAY_RATE_CARDS_URL}/${cardId}/history`,
        isRota: true
      })
      return response
    },
    { enabled: !!cardId }
  )
}
